import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Actions, AppContext, Events, UnityBridge } from "@Context/common/general/context";
import { IncomingReplayEvent } from "@Globals/types";
import { type ReplayDecision } from "@hooks/common/session/replay-decision-utils";
import { warmupReplayLoaderWorker } from "@hooks/game/activity/session/replay-loader-worker-client";
import { buildReplayScenarioTargets } from "@hooks/game/activity/session/session-results.utils";
import useSessionReplayPrefetch from "@hooks/game/activity/session/use-session-replay-prefetch";
import useSessionReplayStream, { ReplayStreamActions } from "@hooks/game/activity/session/use-session-replay-stream";
import {
  buildScenarioKey,
  computeReplayDecisions,
  loadReplayPayload as fetchReplayPayload,
} from "@hooks/game/activity/session/use-session-replay.utils";
import type { SessionResultsData } from "@hooks/game/activity/session/use-session-results.types";
import { useApi } from "@hooks/index";
import { Api } from "@Shared/schemas";
import type { ReplayData } from "@Shared/types/ReplayData";
import { compareVersions } from "compare-versions";

interface UseSessionReplayProps {
  currentTab: number;
  cognitiveResultId: number;
  userId: string;
  sessionResults?: SessionResultsData | null;
}

interface SessionReplayState {
  replayAvailable: boolean;
  isUnityReplayReady: boolean;
  replayData: ReplayData | null;
  decisions: ReplayDecision[];
  areDecisionsLoading: boolean;
  isReplayLoading: boolean;
  isReplayNotFound: boolean;
}
const isReplayNotFoundError = (error: unknown) => {
  if (error === null) {
    return false;
  }

  const { code, message, status } = error as { code?: unknown; message?: unknown; status?: unknown };

  return (
    status === 404 ||
    code === "functions/not-found" ||
    code === "not-found" ||
    (typeof message === "string" && message.includes("error.api.replay-unavailable"))
  );
};
const getReplayEventData = (event: IncomingReplayEvent) => (Array.isArray(event.data) ? event.data : event.data ? [event.data] : []);

const decisionsCache: Record<number, ReplayDecision[]> = {};
const replayAvailabilityCache: Record<number, boolean> = {};
const isUnityReplayReadyCache: Record<number, boolean> = {};
const replayDataCache: Record<number, ReplayData> = {};
const replayDownloadUrlCache: Record<number, string> = {};
const replayDataRequestCache: Record<number, Promise<ReplayData>> = {};
const decisionsRequestCache: Record<number, Promise<ReplayDecision[] | null>> = {};

// @davidrocha: this is a temporary workaround to bypass unity constraints
// In version 2.8.14 we added a gate to avoid entering 3D replay mode before Unity had finished loading the replay on its side
// However, some users might not have updated the app to that version, so we need to bypass that gate for older versions. This can be removed after we are confident most users have updated
const UNITY_REPLAY_READY_CLIENT_VERSION = "2.18.4";

const isClientVersionBeforeUnityReplayReadyGate = (clientVersion: string | null | undefined) => {
  if (!clientVersion) {
    return false;
  }

  try {
    return compareVersions(clientVersion, UNITY_REPLAY_READY_CLIENT_VERSION) === -1;
  } catch {
    return false;
  }
};

const useSessionReplay = ({ currentTab, cognitiveResultId, userId, sessionResults = null }: UseSessionReplayProps) => {
  const { state, dispatch } = useContext(AppContext);
  const currentScenarioKey = buildScenarioKey(currentTab, cognitiveResultId);

  const [replayAvailable, setReplayAvailable] = useState<boolean>(false);
  const [isUnityReplayReady, setIsUnityReplayReady] = useState<boolean>(false);
  const [replayData, setReplayData] = useState<ReplayData | null>(null);
  const [decisions, setDecisions] = useState<ReplayDecision[]>([]);
  const [areDecisionsLoading, setAreDecisionsLoading] = useState<boolean>(false);
  const [isReplayNotFound, setIsReplayNotFound] = useState<boolean>(false);
  const [loadedScenarioKey, setLoadedScenarioKey] = useState<string>("");

  const replayDataRequestRef = useRef<number>(0);
  const activeScenarioKeyRef = useRef<string>("");
  const lastStatusRequestKeyRef = useRef<string>("");

  activeScenarioKeyRef.current = currentScenarioKey;

  const { call: requestReplay } = useApi<typeof Api.Replays.RequestReplay>({
    api: Api.Replays.RequestReplay,
    disableLoader: true,
  });

  const replayScenarioTargets = useMemo(() => buildReplayScenarioTargets(sessionResults), [sessionResults]);
  const shouldBypassUnityReplayReady = isClientVersionBeforeUnityReplayReadyGate(state.app.clientVersion);

  const loadReplayPayload = useCallback(
    async (resultId: number, replayUserId: string, onDownloadUrl?: (downloadUrl: string) => void): Promise<ReplayData> =>
      fetchReplayPayload({
        resultId,
        replayUserId,
        requestReplay,
        onDownloadUrl,
      }),
    [requestReplay]
  );

  const applyCachedReplayState = useCallback((resultId: number, scenarioKey: string) => {
    if (activeScenarioKeyRef.current !== scenarioKey) {
      return;
    }

    if (Object.hasOwn(replayDataCache, resultId)) {
      setReplayData(replayDataCache[resultId]);
    }

    if (Object.hasOwn(decisionsCache, resultId)) {
      setDecisions(decisionsCache[resultId]);
      setAreDecisionsLoading(false);
    }
  }, []);

  const resetReplayState = useCallback(() => {
    setReplayAvailable(false);
    setIsUnityReplayReady(false);
    setReplayData(null);
    setDecisions([]);
    setAreDecisionsLoading(false);
    setIsReplayNotFound(false);
    setLoadedScenarioKey("");
  }, []);

  const setReplayUnavailableState = useCallback((resultId: number) => {
    delete replayAvailabilityCache[resultId];
    delete isUnityReplayReadyCache[resultId];
    delete decisionsCache[resultId];
    setReplayAvailable(false);
    setIsUnityReplayReady(false);
    setReplayData(null);
    setDecisions([]);
    setAreDecisionsLoading(false);
    setIsReplayNotFound(false);
  }, []);

  const { dispatchReplayWebEvent } = useSessionReplayStream();

  const dispatchStartDownload = useCallback(
    (resultId: number, downloadUrl: string) => {
      replayDownloadUrlCache[resultId] = downloadUrl;
      isUnityReplayReadyCache[resultId] = false;
      if (resultId === cognitiveResultId) {
        setIsUnityReplayReady(false);
      }
      dispatchReplayWebEvent(UnityBridge.OutgoingWebEvents.ReplayEvent, {
        action: ReplayStreamActions.START_DOWNLOAD,
        data: {
          cognitive_result_id: resultId,
          download_url: downloadUrl,
        },
      });
    },
    [cognitiveResultId, dispatchReplayWebEvent]
  );

  useEffect(() => {
    const handleReplayEvent = (event: IncomingReplayEvent) => {
      if (event.action !== ReplayStreamActions.REPLAY_READY || event.retry_attempt_id) {
        return;
      }

      const eventData = getReplayEventData(event);
      const resultId = eventData.length > 0 ? eventData.find(data => !data.retry_attempt_id)?.cognitive_result_id : event.cognitive_result_id;
      if (resultId == null) {
        return;
      }

      isUnityReplayReadyCache[resultId] = true;

      if (resultId === cognitiveResultId) {
        setIsUnityReplayReady(true);
      }
    };

    Events.app.replayEvent.on(handleReplayEvent);
    return () => {
      Events.app.replayEvent.remove(handleReplayEvent);
    };
  }, [cognitiveResultId]);

  useEffect(() => {
    if (!cognitiveResultId || !userId || currentTab <= 0) {
      lastStatusRequestKeyRef.current = "";
      resetReplayState();
      return;
    }

    const scenarioKey = buildScenarioKey(currentTab, cognitiveResultId);
    setLoadedScenarioKey(scenarioKey);

    const hasCachedDecisions = Object.hasOwn(decisionsCache, cognitiveResultId);
    if (hasCachedDecisions) {
      setDecisions(decisionsCache[cognitiveResultId]);
      setAreDecisionsLoading(false);
    } else {
      setDecisions([]);
      setAreDecisionsLoading(true);
    }

    const cachedReplayAvailability = replayAvailabilityCache[cognitiveResultId];
    setReplayAvailable(typeof cachedReplayAvailability === "boolean" ? cachedReplayAvailability : false);
    setIsUnityReplayReady(Boolean(isUnityReplayReadyCache[cognitiveResultId]));

    const cachedReplayData = replayDataCache[cognitiveResultId];
    setReplayData(cachedReplayData ?? null);
    setIsReplayNotFound(false);
  }, [cognitiveResultId, currentTab, resetReplayState, userId]);

  useEffect(() => {
    if (!cognitiveResultId || !userId || currentTab <= 0) {
      return;
    }

    const scenarioKey = buildScenarioKey(currentTab, cognitiveResultId);
    warmupReplayLoaderWorker();

    const abortController = new AbortController();
    const { signal } = abortController;

    const handleReplayEvent = (event: IncomingReplayEvent) => {
      if (signal.aborted || activeScenarioKeyRef.current !== scenarioKey || event.action !== "status" || event.retry_attempt_id) {
        return;
      }

      const replayProgress = getReplayEventData(event).find(data => data.cognitive_result_id === cognitiveResultId && !data.retry_attempt_id);
      if (!replayProgress) {
        return;
      }

      setReplayAvailable(prev => {
        const available = prev || (replayProgress.progress ?? 0) >= 1;
        replayAvailabilityCache[cognitiveResultId] = available;
        return available;
      });
    };

    Events.app.replayEvent.on(handleReplayEvent);

    const statusRequestKey = `${scenarioKey}:${userId}`;
    const hasBlobCached = Object.hasOwn(replayDataCache, cognitiveResultId);

    if (hasBlobCached) {
      setReplayAvailable(true);
      replayAvailabilityCache[cognitiveResultId] = true;
    }

    if (lastStatusRequestKeyRef.current !== statusRequestKey) {
      lastStatusRequestKeyRef.current = statusRequestKey;
      dispatch({
        type: Actions.ReplayActions.RequestReplay,
        payload: {
          cognitive_result_id: cognitiveResultId,
          user_id: userId,
        },
        disableLoaders: true,
        onSuccess: response => {
          if (signal.aborted || activeScenarioKeyRef.current !== scenarioKey) {
            return;
          }

          const replayResponse = response.data;
          const downloadUrl = response.download_url;

          if (replayResponse.is_available && !replayResponse.archived_at && downloadUrl) {
            replayDownloadUrlCache[cognitiveResultId] = downloadUrl;
            setReplayAvailable(true);
            replayAvailabilityCache[cognitiveResultId] = true;
            return;
          }

          if (!hasBlobCached) {
            setReplayUnavailableState(cognitiveResultId);
          }
        },
        onError: error => {
          if (signal.aborted || activeScenarioKeyRef.current !== scenarioKey) {
            return;
          }

          console.log("Error fetching replay:", error);
        },
      });
    }

    return () => {
      if (activeScenarioKeyRef.current !== scenarioKey) {
        abortController.abort();
      }
      Events.app.replayEvent.remove(handleReplayEvent);
    };
  }, [cognitiveResultId, currentTab, dispatch, setReplayUnavailableState, userId]);

  useEffect(() => {
    if (!cognitiveResultId || !userId || currentTab <= 0) {
      return;
    }

    const scenarioKey = buildScenarioKey(currentTab, cognitiveResultId);
    const cachedReplayData = replayDataCache[cognitiveResultId];
    const hasCachedDecisions = Object.hasOwn(decisionsCache, cognitiveResultId);

    if (cachedReplayData) {
      setReplayData(cachedReplayData);
      if (hasCachedDecisions) {
        return;
      }
    }

    const requestId = ++replayDataRequestRef.current;

    const loadReplayData = async (): Promise<ReplayData> => {
      if (cachedReplayData) {
        return cachedReplayData;
      }

      return (replayDataRequestCache[cognitiveResultId] ??= loadReplayPayload(cognitiveResultId, userId, downloadUrl =>
        dispatchStartDownload(cognitiveResultId, downloadUrl)
      ).finally(() => {
        delete replayDataRequestCache[cognitiveResultId];
      }));
    };

    const isStaleRequest = () => requestId !== replayDataRequestRef.current || activeScenarioKeyRef.current !== scenarioKey;

    loadReplayData()
      .then(async data => {
        if (isStaleRequest()) {
          applyCachedReplayState(cognitiveResultId, scenarioKey);
          return;
        }

        setReplayData(data);
        replayDataCache[cognitiveResultId] = data;
        setIsReplayNotFound(false);

        if (hasCachedDecisions || Object.hasOwn(decisionsCache, cognitiveResultId)) {
          setDecisions(decisionsCache[cognitiveResultId]);
          setAreDecisionsLoading(false);
          return;
        }

        await Promise.resolve();

        const computedDecisions = await (decisionsRequestCache[cognitiveResultId] ??= computeReplayDecisions(data).finally(() => {
          delete decisionsRequestCache[cognitiveResultId];
        }));
        if (isStaleRequest()) {
          applyCachedReplayState(cognitiveResultId, scenarioKey);
          return;
        }

        if (computedDecisions) {
          setDecisions(computedDecisions);
          decisionsCache[cognitiveResultId] = computedDecisions;
        }

        setAreDecisionsLoading(false);
      })
      .catch(error => {
        applyCachedReplayState(cognitiveResultId, scenarioKey);

        if (isStaleRequest()) {
          return;
        }

        setAreDecisionsLoading(false);

        if (isReplayNotFoundError(error)) {
          delete replayAvailabilityCache[cognitiveResultId];
          delete decisionsCache[cognitiveResultId];
          setReplayAvailable(false);
          setReplayData(null);
          setDecisions([]);
          setIsReplayNotFound(true);
          return;
        }
      });

    return () => {
      if (activeScenarioKeyRef.current !== scenarioKey) {
        replayDataRequestRef.current += 1;
      }
    };
  }, [applyCachedReplayState, cognitiveResultId, currentTab, dispatchStartDownload, loadReplayPayload, userId]);

  useSessionReplayPrefetch({
    currentTab,
    userId,
    scenarioTargets: replayScenarioTargets,
    decisionsCache,
    replayDataCache,
    replayDataRequestCache,
    decisionsRequestCache,
    isReplayNotFoundError,
    loadReplayPayload,
    computeReplayDecisions,
    onReplayDownloadUrl: dispatchStartDownload,
  });

  useEffect(() => {
    return () => {
      setLoadedScenarioKey("");
      setReplayData(null);
      Events.app.showBreadcrumbs.emit();
    };
  }, []);

  const hasReplayContext = Boolean(cognitiveResultId && userId && currentTab > 0);
  const isCurrentScenarioLoaded = loadedScenarioKey === currentScenarioKey;
  const hasCachedDecisions = Object.hasOwn(decisionsCache, cognitiveResultId);
  const hasCachedReplayAvailability = Object.hasOwn(replayAvailabilityCache, cognitiveResultId);
  const hasCachedReplayData = Object.hasOwn(replayDataCache, cognitiveResultId);
  const hasCachedUnityReplayReady = Object.hasOwn(isUnityReplayReadyCache, cognitiveResultId);
  const hasCachedScenarioState = hasCachedDecisions || hasCachedReplayAvailability || hasCachedReplayData || hasCachedUnityReplayReady;
  const cachedReplayAvailable = hasCachedReplayAvailability ? replayAvailabilityCache[cognitiveResultId] : false;
  const cachedUnityReplayReady = hasCachedUnityReplayReady ? isUnityReplayReadyCache[cognitiveResultId] : false;
  const cachedReplayData = hasCachedReplayData ? replayDataCache[cognitiveResultId] : null;
  const cachedDecisions = hasCachedDecisions ? decisionsCache[cognitiveResultId] : [];

  const emptyReplayState: SessionReplayState = {
    replayAvailable: false,
    isUnityReplayReady: false,
    replayData: null,
    decisions: [],
    areDecisionsLoading: false,
    isReplayLoading: false,
    isReplayNotFound: false,
  };

  if (!hasReplayContext) {
    return emptyReplayState;
  }

  if (!isCurrentScenarioLoaded && !hasCachedScenarioState) {
    return {
      ...emptyReplayState,
      areDecisionsLoading: true,
      isReplayLoading: true,
    };
  }

  const effectiveReplayAvailable = (isCurrentScenarioLoaded && replayAvailable) || cachedReplayAvailable;
  const effectiveUnityReplayReady = shouldBypassUnityReplayReady || (isCurrentScenarioLoaded && isUnityReplayReady) || cachedUnityReplayReady;
  const effectiveReplayData = isCurrentScenarioLoaded ? (replayData ?? cachedReplayData) : cachedReplayData;
  const effectiveDecisions = isCurrentScenarioLoaded ? (decisions.length > 0 || !hasCachedDecisions ? decisions : cachedDecisions) : cachedDecisions;
  const effectiveAreDecisionsLoading = hasCachedDecisions ? false : isCurrentScenarioLoaded ? areDecisionsLoading : true;
  const effectiveIsReplayNotFound = isCurrentScenarioLoaded && isReplayNotFound;
  const isReplayLoading = !effectiveReplayData && !effectiveIsReplayNotFound;

  return {
    replayAvailable: effectiveReplayAvailable,
    isUnityReplayReady: effectiveUnityReplayReady,
    replayData: effectiveReplayData,
    decisions: effectiveDecisions,
    areDecisionsLoading: effectiveAreDecisionsLoading,
    isReplayLoading,
    isReplayNotFound: effectiveIsReplayNotFound,
  };
};

export default useSessionReplay;
